import {
  deleteCorporateOnlyLessonDefinition,
  getPaginatedCorporateOnlyLessonDefinitions,
} from '../../../api/corporateOnlyLessonDefinitionApi'
import CorporateOnlyLessonDefinitionFilter from '../../../components/corporate-only-lesson-definitions/corporate-only-lesson-definition-filter'
import LinkButton from '../../../components/link-button/link-button'
import { keysToCamel } from '../../../helpers/keys-to-case'
import { getDayOfWeekLabel, getSearchParams, getSearchValues } from '../../../helpers/utils'
import AuthPermission from '../../../high-order-components/auth-permission'
import translate from '../../../i18n'
import { PageInfo, PermissionsEnum } from '../../../share-types'
import { IStudioTranslation } from '../../studios-container/share-type'
import {
  CorporateOnlyLessonDefinition,
  ICorporateOnlyLessonDefinitionFilterParams,
  IPaginatedCorporateOnlyLessonDefinitionFilterParams,
  PaginatedCorporateOnlyLessonDefinition,
} from '../share-type'
import styles from './corporate-only-lesson-definitions.module.less'
import { PlusOutlined } from '@ant-design/icons'
import { Button, Col, Modal, notification, Row, Space, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { compose } from 'redux'

const defaultPage = {
  pageSize: 10,
  currentPage: 1,
  count: 0,
}

const CorporateOnlyLessonDefinitions = () => {
  const navigate = useNavigate()
  const [corporateOnlyLessonDefinitions, setCorporateOnlyLessonDefinitions] =
    useState<CorporateOnlyLessonDefinition[]>()
  const [pageInfo, setPageInfo] = useState<PageInfo>(defaultPage)
  const [searchValues, setSearchValues] = useState<ICorporateOnlyLessonDefinitionFilterParams>()
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const newSearchValues = getSearchValues(
      [
        { key: 'studioId', isBoolean: false },
        { key: 'size', isBoolean: false, default: defaultPage.pageSize },
        { key: 'page', isBoolean: false, default: defaultPage.currentPage - 1 },
      ],
      searchParams,
    )

    const data = {
      ...newSearchValues,
      size: Number(newSearchValues.size),
      page: Number(newSearchValues.page),
    }
    setSearchValues(newSearchValues)
    getPaginatedCorporateOnlyLessonDefinitionsAction(data)
  }, [])

  const getPaginatedCorporateOnlyLessonDefinitionsAction = (
    data: IPaginatedCorporateOnlyLessonDefinitionFilterParams,
  ) => {
    getPaginatedCorporateOnlyLessonDefinitions(data).then(
      (res: PaginatedCorporateOnlyLessonDefinition) => {
        const formatResponse = res.data.map((item: any) => keysToCamel(item))
        setCorporateOnlyLessonDefinitions(formatResponse)
        setPageInfo({
          ...res.pageInfo,
          currentPage: res.pageInfo.currentPage + 1,
        })
      },
    )
  }

  const onSearch = (values: ICorporateOnlyLessonDefinitionFilterParams) => {
    setSearchValues(values)
    setSearchParams(getSearchParams(values))
    getPaginatedCorporateOnlyLessonDefinitionsAction({
      page: 0,
      size: pageInfo.pageSize,
      ...values,
    })
  }

  const paginationOnChange = (page: number, pageSize: number) => {
    getPaginatedCorporateOnlyLessonDefinitionsAction({
      ...searchValues,
      size: pageSize,
      page: page - 1,
    })
  }

  const deleteCorporateOnlyLessonDefinitionAction = (
    event: React.MouseEvent<HTMLElement>,
    id: number,
  ) => {
    event.stopPropagation()
    Modal.confirm({
      title: translate('corporateOnlyLessonDefinitions.deleteTitle'),
      onOk: async () => {
        await deleteCorporateOnlyLessonDefinition(id)
        notification.success({
          message: translate('corporateOnlyLessonDefinitions.deleteSuccess'),
        })
        getPaginatedCorporateOnlyLessonDefinitionsAction({
          ...searchParams,
          page: 0,
          size: pageInfo.pageSize,
        })
      },
    })
  }

  const columns: ColumnsType<CorporateOnlyLessonDefinition> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id - b.id,
      width: 50,
    },
    {
      title: translate('corporateOnlyLessonDefinitions.studioId'),
      dataIndex: 'studio',
      key: 'studioId',
      render: (studio: { id: number; studioTranslations: IStudioTranslation[] }) => {
        return (
          studio.id +
          '-' +
          studio?.studioTranslations
            .map((studioTranslation: IStudioTranslation) => studioTranslation.name)
            .join('/')
        )
      },
    },
    {
      title: translate('corporateOnlyLessonDefinitions.daysOfWeek'),
      dataIndex: 'daysOfWeek',
      key: 'daysOfWeek',
      render: (daysOfWeek: number[]) =>
        daysOfWeek.map(d => <Tag key={d}>{getDayOfWeekLabel(d)}</Tag>),
    },
    {
      title: translate('corporateOnlyLessonDefinitions.timeRange'),
      dataIndex: 'timeRange',
      key: 'timeRange',
      render: (text: string, record: any) => {
        if (record.timeRangeStart && record.timeRangeEnd) {
          return record.timeRangeStart + ' - ' + record.timeRangeEnd
        } else {
          return ''
        }
      },
    },
    {
      title: translate('tableActionColumn'),
      key: 'action',
      render: (text: string, record: any) => (
        <Space size="middle">
          <AuthPermission permission={PermissionsEnum.CORPORATE_ONLY_LESSON_DEFINITIONS_WRITE}>
            <LinkButton to={`/corporate-only-lesson-definitions/${record.id}/edit`}>
              {translate('edit')}
            </LinkButton>
          </AuthPermission>

          <LinkButton to={`/corporate-only-lesson-definitions/${record.id}`}>
            {translate('view')}
          </LinkButton>

          <AuthPermission permission={PermissionsEnum.CORPORATE_ONLY_LESSON_DEFINITIONS_WRITE}>
            <Button
              danger
              type="link"
              block
              onClick={event => deleteCorporateOnlyLessonDefinitionAction(event, record.id)}
            >
              {translate('delete')}
            </Button>
          </AuthPermission>
        </Space>
      ),
    },
  ]

  return (
    <Space className={styles['list-space']} orientation="vertical" size={16}>
      <Row justify="center" align="middle">
        <Col xs={{ span: 24 }} sm={{ span: 12 }} className={styles['left-col']}>
          <div>{translate('corporateOnlyLessonDefinitions.listTitle')}</div>
        </Col>
        <Col xs={{ span: 24 }} sm={{ span: 12 }} className={styles['right-col']}>
          <AuthPermission permission={PermissionsEnum.CORPORATE_ONLY_LESSON_DEFINITIONS_WRITE}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/corporate-only-lesson-definitions/new')}
            >
              {translate('corporateOnlyLessonDefinitions.createTitle')}
            </Button>
          </AuthPermission>
        </Col>
      </Row>

      <div className={styles['filter-bar']}>
        <CorporateOnlyLessonDefinitionFilter onSearch={onSearch} formData={searchValues} />
      </div>

      <Table
        rowKey={record => record.id}
        columns={columns}
        dataSource={corporateOnlyLessonDefinitions}
        pagination={{
          current: pageInfo.currentPage,
          pageSize: pageInfo.pageSize,
          total: pageInfo.count,
          onChange: paginationOnChange,
        }}
      />
    </Space>
  )
}

export default compose()(CorporateOnlyLessonDefinitions)
